/* eslint-disable linebreak-style */
/* eslint-disable react/jsx-filename-extension */
import React from 'react';
import { Fade } from 'react-awesome-reveal';

import MushroomEdibleImg from '../../assets/images/florai/mushroom-edible.jpg';
import MushroomToxicImg from '../../assets/images/florai/mushroom-toxic.jpg';

// Safety band — edible vs deadly look-alike comparison plus first-aid promise
export default function FloraiSafety() {
  return (
    <section id="florai-safety" className="florai px-4 !mb-4">
      <div className="bg-[#24361B] text-[#EEF2EC] rounded-[32px] py-14 sm:py-20 px-5 sm:px-8 max-w-[1400px] mx-auto">
        <div className="max-w-[1120px] mx-auto">
          <Fade direction="up" triggerOnce>
            <p className="text-[.74rem] font-bold tracking-[.16em] uppercase text-[#98CC6B] mb-3">Safety first</p>
            <h2 className="text-[clamp(1.9rem,3.8vw,2.8rem)] leading-tight mb-3.5 tracking-tight !font-semibold text-[#EEF2EC]">
              Looks the same.
              {' '}
              <em className="font-instrument-serif !font-normal text-[1.08em]">Isn&apos;t.</em>
            </h2>
            <p className="text-[#B9C6B0] max-w-2xl mb-10 text-[1.05rem]">
              Every mushroom season, people in Đà Lạt and Vietnam&apos;s mountain provinces are
              hospitalized because a toxic species mimicked an edible one. FlorAI is trained on the
              toxic species of Vietnamese forests — and it never gambles.
            </p>
          </Fade>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
            <Fade direction="up" triggerOnce>
              <div className="h-full bg-[#2C4222] border border-[#3B5230] rounded-[22px] p-4 sm:p-6">
                <img
                  src={MushroomEdibleImg}
                  alt="Cluster of edible honey-colored mushrooms in the forest"
                  loading="lazy"
                  className="w-full h-36 object-cover rounded-[14px] mb-4"
                />
                <div className="flex items-center flex-wrap gap-3 sm:gap-4 mb-3.5">
                  <svg width="46" height="46" viewBox="0 0 48 48" aria-hidden="true" className="flex-none">
                    <path d="M6 24c0-10 8-16 18-16s18 6 18 16H6z" fill="#98CC6B" />
                    <rect x="20" y="24" width="8" height="16" rx="3.5" fill="#DDEFD2" />
                  </svg>
                  <div className="min-w-0">
                    <b className="text-[1.1rem] block break-words">Termitomyces — “nấm mối”</b>
                    <span className="text-[.8rem] text-[#A4B69A] italic">Prized edible, fruits after rain</span>
                  </div>
                  <span className="text-[.7rem] font-bold tracking-[.1em] uppercase px-3 py-1 rounded-full ml-auto flex-none bg-[#98CC6B] text-[#24361B]">Edible</span>
                </div>
                <ul className="m-0 pl-[18px] text-[#C6D4BD] text-[.92rem] list-disc">
                  <li className="mb-1.5">Grows from termite mounds, firm cap</li>
                  <li className="mb-1.5">Harvested widely in the Central Highlands</li>
                  <li>FlorAI confirms species and shows harvest notes</li>
                </ul>
              </div>
            </Fade>
            <Fade direction="up" delay={120} triggerOnce>
              <div className="h-full bg-gradient-to-b from-[#4A2E1A] to-[#2C4222] border border-[#7A4A28] rounded-[22px] p-4 sm:p-6">
                <img
                  src={MushroomToxicImg}
                  alt="Innocent-looking wild toadstools that mimic edible species"
                  loading="lazy"
                  className="w-full h-36 object-cover rounded-[14px] mb-4"
                />
                <div className="flex items-center flex-wrap gap-3 sm:gap-4 mb-3.5">
                  <svg width="46" height="46" viewBox="0 0 48 48" aria-hidden="true" className="flex-none">
                    <path d="M6 24c0-10 8-16 18-16s18 6 18 16H6z" fill="#ED7A3B" />
                    <circle cx="17" cy="16" r="2.4" fill="#FBE6D6" />
                    <circle cx="27" cy="12" r="2" fill="#FBE6D6" />
                    <circle cx="33" cy="18" r="2.2" fill="#FBE6D6" />
                    <rect x="20" y="24" width="8" height="16" rx="3.5" fill="#F3DFCE" />
                  </svg>
                  <div className="min-w-0">
                    <b className="text-[1.1rem] block break-words">Amanita &amp; toxic mimics</b>
                    <span className="text-[.8rem] text-[#A4B69A] italic">Death cap group, Chlorophyllum…</span>
                  </div>
                  <span className="text-[.7rem] font-bold tracking-[.1em] uppercase px-3 py-1 rounded-full ml-auto flex-none bg-[#ED7A3B] text-white">Deadly</span>
                </div>
                <ul className="m-0 pl-[18px] text-[#C6D4BD] text-[.92rem] list-disc">
                  <li className="mb-1.5">Nearly identical to edible species at a glance</li>
                  <li className="mb-1.5">Symptoms can appear 6–24 hours after eating</li>
                  <li>FlorAI flags the look-alike risk before you pick</li>
                </ul>
              </div>
            </Fade>
          </div>

          <Fade direction="up" triggerOnce>
            <div className="flex gap-4 items-start bg-[#2C4222] border border-[#3B5230] rounded-[22px] px-6 py-5 text-[.95rem] text-[#D3DFCA]">
              <span className="flex-none w-10 h-10 rounded-xl bg-[#ED7A3B] text-white grid place-items-center font-extrabold text-[1.2rem]" aria-hidden="true">+</span>
              <div>
                <b className="text-white">Built-in first-aid guidance.</b>
                {' '}
                If poisoning is suspected, FlorAI shows step-by-step first aid, what to bring to the
                hospital (a sample of the mushroom!), and the nearest poison-control contacts — even
                offline.
                {' '}
                <b className="text-white">It never marks a risky species as “safe to eat”.</b>
              </div>
            </div>
          </Fade>
        </div>
      </div>
    </section>
  );
}
